import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  Clock3,
  Copy,
  Sparkles,
  Play,
  Pause,
  Download,
  MoreHorizontal,
  Trash2,
  RectangleHorizontal,
  Scan,
  Cpu,
  Clapperboard,
  Loader2,
} from "lucide-react";
import {
  getAiVideoTaskDetailApi,
  getAiVideoTemplateDetailApi,
  deleteAiVideoTaskApi,
} from "../../service/aiVideo";
import { downloadFileSmart } from "../../utils/batchVideoDownload";
import { VIDEO_CDN_PREFIX } from "../../config/video";
import { AppToast } from "../../components/common/AlertMessage";
import "./AiVideoDetail.scss";

/** 参数展示项键 */
type ParamKey = "ratio" | "resolution" | "mode" | "duration" | "model";

/** 参数展示项 */
interface ParamItem {
  key: ParamKey;
  label: string;
  value: string;
  icon: "ratio" | "resolution" | "mode" | "duration" | "model";
}

/** 详情页展示数据 */
interface DetailItem {
  title: string;
  mediaUrl: string;
  isVideo: boolean;
  prompt: string;
  params: ParamItem[];
  createdAt: string;
  ratio: string;
  /** 预估消耗积分（仅作品详情有值） */
  estimatedPoint?: number;
  /** 后端返回的输入素材列表（"做同款/再次编辑"时回填到工作台） */
  inputFiles?: AiVideo.InputFile[];
  /**
   * 后端原始生成模式（如 "FIRST_FRAME"、"REFERENCE"）
   * "做同款"时传给工作台用 API_TO_MODE 映射切换到对应 tab
   */
  generationMode?: string;
}

/** 空数据兜底 */
const emptyItem: DetailItem = {
  title: "",
  mediaUrl: "",
  isVideo: false,
  prompt: "",
  params: [],
  createdAt: "",
  ratio: "16:9",
  estimatedPoint: undefined,
  inputFiles: undefined,
};

/** 生成模式中文标签映射 */
const MODE_LABELS: Record<string, string> = {
  TEXT_TO_VIDEO: "文生视频",
  FIRST_FRAME: "首帧",
  LAST_FRAME: "尾帧",
  FIRST_LAST_FRAME: "首尾帧",
  REFERENCE: "多模态参考",
  TEXT: "文生视频",
};

/** 将时间字符串格式化为 yyyy/MM/dd HH:mm */
const formatGeneratedTime = (value?: string): string => {
  if (!value) return "—";
  const date = new Date(value.includes("T") ? value : value.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** 解析媒体地址：http 升级 https，相对路径拼 CDN 前缀 */
const resolveMediaUrl = (rawUrl?: string): string => {
  if (!rawUrl) return "";
  // 清理后端可能返回的反引号/引号包裹（如 "`http://...`"）
  const cleaned = rawUrl.trim().replace(/^["'`]+|["'`]+$/g, "");
  if (!cleaned) return "";
  if (cleaned.startsWith("http://") || cleaned.startsWith("https://"))
    return cleaned;
  const path = cleaned.startsWith("/") ? cleaned : `/${cleaned}`;
  return `${VIDEO_CDN_PREFIX}${path}`;
};

/** 从任务数据提取媒体地址（视频优先于音频） */
const extractMediaUrl = (
  t: AiVideo.TaskRespVO,
): { url: string; isVideo: boolean } => {
  if (t.videoUrl || t.outputVideoUrl) {
    return {
      url: resolveMediaUrl(t.videoUrl || t.outputVideoUrl),
      isVideo: true,
    };
  }
  if (t.outputAudioUrl) {
    return { url: resolveMediaUrl(t.outputAudioUrl), isVideo: false };
  }
  return { url: "", isVideo: false };
};

/** 从任务数据提取生成模式中文标签 */
const extractModeLabel = (
  t: AiVideo.TaskRespVO | AiVideo.TemplateRespVO,
): string => {
  const generationMode = (t as AiVideo.TaskRespVO).generationMode;
  if (generationMode && MODE_LABELS[generationMode])
    return MODE_LABELS[generationMode];
  const referenceType = (t as AiVideo.TaskRespVO).referenceType;
  if (referenceType && MODE_LABELS[referenceType])
    return MODE_LABELS[referenceType];
  return "文生视频";
};

/** 构建参数展示列表 */
const buildParams = (
  ratio: string,
  resolution: string,
  modeLabel: string,
  duration: number | string,
): ParamItem[] => {
  const durationText =
    typeof duration === "string"
      ? duration
      : duration > 0
        ? `${duration} 秒`
        : "—";
  return [
    {
      key: "ratio",
      label: "画面比例",
      value: ratio === "adaptive" ? "自适应" : ratio || "16:9",
      icon: "ratio",
    },
    {
      key: "resolution",
      label: "分辨率",
      value: resolution || "2K",
      icon: "resolution",
    },
    { key: "mode", label: "生成模式", value: modeLabel, icon: "mode" },
    {
      key: "duration",
      label: "视频时长",
      value: durationText,
      icon: "duration",
    },
    { key: "model", label: "模型引擎", value: "MiniMax-H3", icon: "model" },
  ];
};

/** 将任务数据转为详情页展示数据 */
const toWorkDetail = (task: AiVideo.TaskRespVO): DetailItem => {
  const { url, isVideo } = extractMediaUrl(task);
  const prompt = task.prompt || task.script || "";
  const modeLabel = extractModeLabel(task);
  const ratio = task.aspectRatio || "16:9";
  const duration = task.duration ?? task.originalDuration ?? 0;
  return {
    title: prompt.replace(/\s+/g, " ").trim().slice(0, 22) || "未命名作品",
    mediaUrl: url,
    isVideo,
    prompt,
    params: buildParams(ratio, task.resolution || "2K", modeLabel, duration),
    createdAt: formatGeneratedTime(task.createTime),
    ratio,
    estimatedPoint: task.estimatedPoint,
    inputFiles: task.inputFiles,
    // 保存后端原始生成模式（generationMode 优先，兜底 referenceType）
    generationMode: task.generationMode || task.referenceType,
  };
};

/** 将模板数据转为详情页展示数据 */
const toTemplateDetail = (template: AiVideo.TemplateRespVO): DetailItem => {
  const url = resolveMediaUrl(
    template.videoUrl || template.outputVideoUrl || template.outputAudioUrl,
  );
  const isVideo = Boolean(template.videoUrl || template.outputVideoUrl);
  const prompt = template.prompt || template.script || "";
  const modeLabel = extractModeLabel(template);
  const ratio = template.aspectRatio || "16:9";
  const duration = template.duration ?? 5;
  return {
    title: template.title || template.description?.slice(0, 22) || "未命名模板",
    mediaUrl: url,
    isVideo,
    prompt,
    params: buildParams(
      ratio,
      template.resolution || "2K",
      modeLabel,
      duration,
    ),
    createdAt: "",
    ratio,
    // 模板预计消耗积分（后端模板数据里的 estimatedPoint，如 120）
    estimatedPoint: template.estimatedPoint,
    inputFiles: template.inputFiles,
    // 保存后端原始生成模式（generationMode 优先，兜底 referenceType）
    generationMode: template.generationMode || template.referenceType,
  };
};

/**
 * “做同款”跳转时通过 location.state 携带的模板数据形状
 * 与 Index.tsx 中 TemplateItem 结构兼容，仅声明需要使用的字段
 */
interface TemplateState {
  templateId: string;
  title: string;
  ratio: string;
  prompt: string;
  poster: string;
  duration: number;
  videoUrl?: string;
  raw?: AiVideo.TemplateRespVO;
}

/**
 * 将 state 携带的模板数据转为详情页展示数据
 * 优先用 raw（后端原始数据）转换；raw 缺失时用 template 字段兜底
 */
const toTemplateStateDetail = (tpl: TemplateState): DetailItem => {
  // 有 raw 时复用后端数据转换逻辑，保证字段完整
  if (tpl.raw) return toTemplateDetail(tpl.raw);
  // 无 raw（本地兜底卡片）时用 template 字段构造
  const url = resolveMediaUrl(tpl.videoUrl);
  const isVideo = Boolean(tpl.videoUrl);
  const ratio = tpl.ratio || "16:9";
  const duration = tpl.duration ?? 5;
  return {
    title: tpl.title || "未命名模板",
    mediaUrl: url,
    isVideo,
    prompt: tpl.prompt,
    params: buildParams(ratio, "2K", "文生视频", duration),
    createdAt: "",
    ratio,
  };
};

/** 根据参数图标名返回对应 lucide 图标组件 */
const renderParamIcon = (icon: ParamItem["icon"]) => {
  switch (icon) {
    case "ratio":
      return <RectangleHorizontal />;
    case "resolution":
      return <Scan />;
    case "mode":
      return <Sparkles />;
    case "duration":
      return <Clock3 />;
    case "model":
      return <Cpu />;
    default:
      return <Clapperboard />;
  }
};

export const AiVideoDetail: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [query] = useSearchParams();
  const workId = query.get("work");
  const templateId = query.get("template");
  const isWork = Boolean(workId);

  const [item, setItem] = useState<DetailItem>(emptyItem);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  /** 删除确认弹窗状态（与作品列表卡片一致） */
  const [confirming, setConfirming] = useState(false);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  // 删除作品后延迟跳转的定时器引用（组件卸载时清理，避免在已卸载组件上 navigate）
  const deleteTimerRef = useRef<number | null>(null);
  // 视频播放器引用与播放状态：用于控制中央播放按钮的显隐与切换
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // 自适应比例下从视频元数据探测到的真实宽高（用于修正 16:9 误判）
  const [detectedRatio, setDetectedRatio] = useState<string | null>(null);

  /** 切换视频播放/暂停 */
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play();
    } else {
      video.pause();
    }
  }, []);

  /**
   * 加载详情数据
   * - 作品详情：调用 GET /ai-video/tasks/{id}
   * - 模板详情：优先用 location.state 携带的模板数据渲染（“做同款”跳转），
   *   若 URL 参数为有效数字 id 再调用 GET /ai-video/templates/{id} 刷新
   */
  useEffect(() => {
    let active = true;
    const id = Number(isWork ? workId : templateId);

    // 模板详情：先尝试用 state 数据立即渲染，避免空白等待
    if (!isWork) {
      const state = location.state as { template?: TemplateState } | null;
      if (state?.template) {
        if (active) {
          setItem(toTemplateStateDetail(state.template));
          setLoading(false);
        }
        // URL 参数为有效数字 id 时，调接口刷新为后端最新数据
        if (Number.isFinite(id) && id > 0) {
          const refresh = async () => {
            try {
              const res = await getAiVideoTemplateDetailApi(id);
              if (active && res.code === 0 && res.data) {
                setItem(toTemplateDetail(res.data as AiVideo.TemplateRespVO));
              }
            } catch {
              /* 接口刷新失败时保留 state 数据，不阻塞展示 */
            }
          };
          void refresh();
        }
        return () => {
          active = false;
        };
      }
    }

    // 作品详情或无 state 的模板详情：直接调接口
    const load = async () => {
      setLoading(true);
      try {
        if (!Number.isFinite(id) || id <= 0) {
          setItem(emptyItem);
          setToast("ID 无效，详情加载失败");
          return;
        }
        const res = isWork
          ? await getAiVideoTaskDetailApi(id)
          : await getAiVideoTemplateDetailApi(id);
        if (active) {
          if (res.code === 0 && res.data) {
            setItem(
              isWork
                ? toWorkDetail(res.data as AiVideo.TaskRespVO)
                : toTemplateDetail(res.data as AiVideo.TemplateRespVO),
            );
          } else {
            setItem(emptyItem);
            setToast(res.msg || "详情加载失败");
          }
        }
      } catch (error) {
        if (active)
          setToast(error instanceof Error ? error.message : "详情加载失败");
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [isWork, templateId, workId, location.state]);

  /** Toast 自动消失 */
  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(""), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  /** 点击外部关闭更多菜单 */
  useEffect(() => {
    if (!moreMenuOpen) return undefined;
    const handler = (e: MouseEvent) => {
      if (
        moreMenuRef.current &&
        !moreMenuRef.current.contains(e.target as Node)
      ) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [moreMenuOpen]);

  /** 组件卸载时清理删除后延迟跳转的定时器，避免在已卸载组件上触发 navigate */
  useEffect(() => {
    return () => {
      if (deleteTimerRef.current !== null) {
        window.clearTimeout(deleteTimerRef.current);
        deleteTimerRef.current = null;
      }
    };
  }, []);

  /** 根据比例计算 CSS aspect-ratio，并判断是否竖屏
   *  - adaptive 自适应：优先用视频元数据探测的真实宽高比，否则降级 16:9
   *  - 其它解析失败的比例也兜底为 16:9
   */
  const frameStyle = useMemo(() => {
    // adaptive 时优先用探测结果
    const effectiveRatio =
      (item.ratio === "adaptive" && detectedRatio) || item.ratio || "16:9";
    const ratioParts = String(effectiveRatio)
      .split(":")
      .map(Number);
    const w = ratioParts[0] || 16;
    const h = ratioParts[1] || 9;
    return {
      aspectRatio: `${w} / ${h}`,
      isPortrait: h > w,
    };
  }, [item.ratio, detectedRatio]);

  /** 视频元数据加载完成时探测真实宽高比（修正 adaptive 自适应比例误判） */
  const handleVideoLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    // 仅当 adaptive 且未探测到比例时写入，避免覆盖已确认的比例
    if (w > 0 && h > 0 && item.ratio === "adaptive" && !detectedRatio) {
      setDetectedRatio(`${w}:${h}`);
    }
  }, [item.ratio, detectedRatio]);

  /** 返回列表页 */
  const back = useCallback(() => {
    navigate(`/hub/ai-video${isWork ? "?tab=works" : ""}`);
  }, [navigate, isWork]);

  /** 复制提示词到剪贴板 */
  const copyPrompt = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(item.prompt);
    } catch {
      const input = document.createElement("textarea");
      input.value = item.prompt;
      document.body.append(input);
      input.select();
      document.execCommand("copy");
      input.remove();
    }
    setToast("完整提示词已复制");
  }, [item.prompt]);

  /** 下载媒体文件 */
  const handleDownload = useCallback(() => {
    if (!item.mediaUrl) {
      setToast("当前作品暂无可导出的视频文件");
      return;
    }
    const ext = item.isVideo ? "mp4" : "mp3";
    downloadFileSmart(
      item.mediaUrl,
      `ai-video-${isWork ? "work" : "template"}-${Date.now()}.${ext}`,
    );
    setToast("视频导出已开始");
  }, [item.mediaUrl, item.isVideo, isWork]);

  /** 确认删除作品（去掉 window.confirm，改用确认弹窗） */
  const handleDelete = useCallback(async () => {
    setConfirming(false);
    setMoreMenuOpen(false);
    if (!isWork || !workId) return;
    setDeleting(true);
    try {
      const res = await deleteAiVideoTaskApi(Number(workId));
      if (res.code === 0) {
        setToast("作品已删除");
        deleteTimerRef.current = window.setTimeout(
          () => navigate("/hub/ai-video?tab=works"),
          1200,
        );
      } else {
        setToast(res.msg || "删除失败");
      }
    } catch (error) {
      setToast(error instanceof Error ? error.message : "删除失败");
    } finally {
      setDeleting(false);
    }
  }, [isWork, workId, navigate]);

  /** 点击"做同款/再次编辑"跳转工作台 */
  const handlePrimaryAction = useCallback(() => {
    // 将详情数据写入 localStorage 供工作台读取回显（含 inputFiles 素材回填）
    try {
      localStorage.setItem(
        "ai_video_detail_apply",
        JSON.stringify({
          prompt: item.prompt,
          ratio: item.ratio,
          // 后端原始生成模式（如 "FIRST_FRAME"），工作台用 API_TO_MODE 映射切换 tab
          generationMode: item.generationMode || "",
          resolution:
            item.params.find((p) => p.key === "resolution")?.value || "",
          duration: item.params.find((p) => p.key === "duration")?.value || "",
          inputFiles: item.inputFiles,
          source: isWork ? "work" : "template",
        }),
      );
    } catch {
      /* localStorage 写入失败忽略 */
    }
    navigate("/hub/ai-video");
  }, [item, isWork, navigate]);

  return (
    <div className="ai-video-detail-page">
      {/* 顶栏：返回< + AI视频标题 + 作品详情胶囊 | 导出 + 三点菜单 */}
      <header className="home-series__header">
        <div className="home-series__header-left">
          <div className="home-series__header-leading">
            <button
              type="button"
              className="home-series__back"
              aria-label="返回上一页"
              onClick={() => navigate(-1)}
            >
              <ChevronLeft size={20} />
            </button>
          </div>
        </div>
        <div className="home-series__header-right detail-header-actions">
          {/* 导出按钮：下载视频 */}
          <button
            type="button"
            className="detail-export-btn"
            onClick={handleDownload}
          >
            <Download size={14} />
            导出
          </button>
          {/* 三点菜单：仅作品详情展示删除操作，模板详情不展示 */}
          {isWork ? (
          <div className="detail-more-wrap">
            <button
              type="button"
              className="detail-more-trigger"
              aria-label="更多操作"
              onClick={() => {
                if (!confirming) setMoreMenuOpen((v) => !v);
              }}
            >
              <MoreHorizontal size={18} />
            </button>
            {moreMenuOpen && !confirming && (
              <div className="detail-more-popover">
                <button
                  type="button"
                  className="detail-more-item danger"
                  onClick={() => setConfirming(true)}
                  disabled={deleting || !isWork}
                >
                  <Trash2 size={14} />
                  删除作品
                </button>
              </div>
            )}
            {confirming && (
              <div className="detail-confirm-popover">
                <span>确认删除？</span>
                <button
                  type="button"
                  className="confirm-yes"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  删除
                </button>
                <button
                  type="button"
                  className="confirm-no"
                  onClick={() => setConfirming(false)}
                >
                  取消
                </button>
              </div>
            )}
          </div>
          ) : null}
        </div>
      </header>
      <div className="detail-scroll">
        <div className="detail-shell">
          <div className="detail-grid">
            {/* Hero 标题区 */}
            <section className="detail-hero">
              <h1 title={item.title || (isWork ? "我的作品" : "视频模板")}>
                {item.title || (isWork ? "我的作品" : "视频模板")}
              </h1>
              {/* 生成时间：仅作品详情展示，模板详情无生成时间 */}
              {isWork ? (
                <p className="generated-time">
                  <Clock3 />
                  <span>生成于 {item.createdAt || "—"}</span>
                </p>
              ) : null}
            </section>

            {/* 视频预览卡 */}
            <section className="preview-card" aria-label="视频预览">
              {loading ? (
                <div className="detail-loading">
                  <Loader2 className="animate-spin" size={22} />
                </div>
              ) : (
                <div className="preview-stage">
                  <div
                    className={`video-frame${frameStyle.isPortrait ? " portrait" : ""}`}
                    style={{ aspectRatio: frameStyle.aspectRatio }}
                  >
                    {item.mediaUrl && item.isVideo ? (
                      <video
                        ref={videoRef}
                        src={`${item.mediaUrl}#t=0.1`}
                        controls
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        onLoadedMetadata={handleVideoLoadedMetadata}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                      />
                    ) : (
                      <span className="stage-placeholder">
                        {item.mediaUrl ? "音频预览" : "暂无媒体内容"}
                      </span>
                    )}
                    <button
                      className={`detail-play${isPlaying ? " detail-play--hidden" : ""}`}
                      type="button"
                      aria-label={isPlaying ? "暂停" : "播放预览"}
                      onClick={togglePlay}
                    >
                      {isPlaying ? <Pause /> : <Play />}
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* 详情内容区 */}
            <div className="detail-content">
              {/* 完整提示词 */}
              <section className="detail-section">
                <div className="section-title-row">
                  <div>
                    <h2>完整提示词</h2>
                    <small>Prompt</small>
                  </div>
                  <button
                    className="copy-button"
                    type="button"
                    onClick={copyPrompt}
                  >
                    <Copy />
                    <span>复制</span>
                  </button>
                </div>
                <div className="prompt-box">
                  <p>{item.prompt || "—"}</p>
                </div>
              </section>

              {/* 生成参数 */}
              <section className="detail-section">
                <div className="section-title-row">
                  <div>
                    <h2>生成参数</h2>
                    <small>Generation settings</small>
                  </div>
                </div>
                <div className="parameter-grid">
                  {item.params.map((param) => (
                    <div className="parameter" key={param.key}>
                      <span className="parameter-icon">
                        {renderParamIcon(param.icon)}
                      </span>
                      <div>
                        <small>{param.label}</small>
                        <strong>{param.value}</strong>
                      </div>
                    </div>
                  ))}
                </div>
                {/* 积分消耗：作品详情展示实际消耗，模板详情展示预计消耗 */}
                <div className="billing-note">
                  <span>
                    创作预计消耗{" "}
                    <em className="billing-point">
                      {item.estimatedPoint ?? 0}
                    </em>{" "}
                    积分
                  </span>
                  <strong>MiniMax H3</strong>
                </div>
              </section>

              {/* 底部操作区 */}
              <footer className="action-area">
                <p className="action-hint">
                  {isWork
                    ? "返回视频工作台后，可基于本次提示词和参数继续调整并再次生成。"
                    : "进入视频工作台后，仍可继续调整提示词、画面比例、模型与生成模式。"}
                </p>
                <button
                  className="primary-action"
                  type="button"
                  onClick={handlePrimaryAction}
                >
                  <Sparkles />
                  <span>{isWork ? "再次编辑" : "做同款"}</span>
                </button>
              </footer>
            </div>
          </div>
        </div>
      </div>

      {/* Toast 提示（使用 AppToast 组件，与 AI 生图一致） */}
      <AppToast
        config={
          toast ? { type: "info", message: toast, closable: false } : null
        }
        onClose={() => setToast("")}
      />
    </div>
  );
};
